import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Injectable } from '@angular/core';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog, MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { RolePermissionsService } from '@shared/services/auth/role-permissions.service';

import { ApiBaseService } from '@shared/services/entities/api-base.service';
import { PageLoaderComponent, PageLoad } from '@app/shared/components/page-loader/page-loader.component';
import { PageTitleComponent } from '@app/shared/components/page-title/page-title.component';
import { SearchPillComponent } from '@app/shared/components/filter-bar/search-pill.component';
import { KpiStripComponent, ListKpi } from '@app/shared/components/kpi-strip/kpi-strip.component';
import { currentMonthStart, currentMonthEnd } from '@app/shared/utils/date-range';
import { UserScopeService, UserScope, inScope } from '@app/shared/services/user-scope.service';
import { CustomMessageService } from '@shared/components/custom-message/custom-message.service';
import { SimpleExcelService } from '@app/shared/services/simple-excel.service';
import { PlacesService } from '@shared/services/entities/places.service';
import { liveReload } from '@shared/services/realtime.service';

/** Reportes consolidados que calcula el API (GET /reports/...). */
@Injectable({ providedIn: 'root' })
export class ConsolidatedReportsService extends ApiBaseService<any> {
  constructor() { super('reports'); }
  async get(report: string, params: Record<string, string>): Promise<any> {
    const q = Object.entries(params).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');
    const r: any = await this.request<any>(`reports/${report}?${q}`, 'GET');
    return Array.isArray(r?.data) ? r.data[0] : r?.data;
  }
  /** OC y factura de una remisión (Consolidación de remisiones). */
  async saveBilling(body: { id: string; purchaseOrder: string | null; invoiceNumber: string | null; invoiceDate: string | null;
                            billingComments: string | null; billingIssue: boolean }): Promise<any> {
    const r: any = await this.request<any>('reports/remissionBilling', 'POST', body);
    return Array.isArray(r?.data) ? r.data[0] : r?.data;
  }
}

/** Captura de OC, factura y comentarios de una remisión. */
@Component({
  selector: 'app-remission-billing-dialog',
  standalone: true,
  imports: [FormsModule, MatDialogModule, MatFormFieldModule, MatInputModule, MatButtonModule, MatIconModule, MatCheckboxModule, MatDatepickerModule],
  template: `
<h2 mat-dialog-title>Facturación · Remisión {{ data.remission }}</h2>
<mat-dialog-content>
  <p style="margin:0 0 12px;font-size:13px;color:#555">{{ data.hospital }} · {{ data.doctor }} · Folio de CX {{ data.surgeryFolio }}</p>
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:4px 16px;min-width:min(520px,80vw)">
    <mat-form-field appearance="outline">
      <mat-label>OC</mat-label>
      <input matInput [(ngModel)]="po" maxlength="60" autocomplete="off" placeholder="p. ej. 4500068566 o sin OC en Enova">
    </mat-form-field>
    <mat-form-field appearance="outline">
      <mat-label>Número de factura</mat-label>
      <input matInput [(ngModel)]="invoice" maxlength="60" autocomplete="off">
      <mat-hint>Vacío = PENDIENTE</mat-hint>
    </mat-form-field>
    <mat-form-field appearance="outline">
      <mat-label>Fecha de factura</mat-label>
      <input matInput [matDatepicker]="dp" [(ngModel)]="invoiceDate" [disabled]="!invoice.trim()">
      <mat-datepicker-toggle matSuffix [for]="dp"></mat-datepicker-toggle>
      <mat-datepicker #dp></mat-datepicker>
    </mat-form-field>
    <mat-checkbox color="warn" [(ngModel)]="issue" [disabled]="!!invoice.trim()" style="align-self:center">Con novedad</mat-checkbox>
    <mat-form-field appearance="outline" style="grid-column:1/-1">
      <mat-label>Comentarios</mat-label>
      <textarea matInput rows="2" [(ngModel)]="comments" maxlength="500"></textarea>
    </mat-form-field>
  </div>
  <p style="margin:0;font-size:12px;color:#777">Aplica a todos los renglones de la remisión. Al capturar la factura la remisión queda Facturada.</p>
</mat-dialog-content>
<mat-dialog-actions align="end">
  <button mat-button (click)="ref.close(false)">Cancelar</button>
  <button mat-flat-button color="primary" (click)="save()" [disabled]="saving"><mat-icon>save</mat-icon> Guardar</button>
</mat-dialog-actions>
  `,
})
export class RemissionBillingDialogComponent {
  readonly data = inject<any>(MAT_DIALOG_DATA);
  readonly ref = inject(MatDialogRef<RemissionBillingDialogComponent>);
  private readonly service = inject(ConsolidatedReportsService);
  private readonly msg = inject(CustomMessageService);
  po = this.data.purchaseOrder ?? '';
  invoice = this.data.invoice && !['PENDIENTE', 'CON NOVEDAD'].includes(this.data.invoice) ? this.data.invoice : '';
  invoiceDate: Date | null = this.data.invoiceDate ? new Date(this.data.invoiceDate + 'T12:00:00') : null;
  issue = !!this.data.billingIssue;
  comments = this.data.comments ?? '';
  saving = false;

  async save(): Promise<void> {
    this.saving = true;
    try {
      const inv = this.invoice.trim();
      await this.service.saveBilling({
        id: this.data.remissionId, purchaseOrder: this.po.trim() || null, invoiceNumber: inv || null,
        invoiceDate: inv && this.invoiceDate ? day(this.invoiceDate) : null,
        billingComments: this.comments.trim() || null, billingIssue: !inv && this.issue,
      });
      this.ref.close(true);
    } catch (err: any) {
      await this.msg.open({ title: 'Error', type: 'error', message: err?.message || 'No se pudo guardar.', dismissBtn: true });
    } finally {
      this.saving = false;
    }
  }
}

type Col = { key: string; name: string; num?: boolean };
interface ReportDef {
  api: string; title: string; icon: string; noun: string; search: string;
  cols: Col[]; live: string[]; file: string;
}

const REPORTS: Record<string, ReportDef> = {
  incidentsByStatus: {
    api: 'incidentsByStatus', title: 'Incidencias por estado', icon: 'report_problem', noun: 'incidencias', file: 'Incidencias-por-estado',
    search: 'Folio, hospital, código, lote, MPXR, descripción…', live: ['incidents'],
    cols: [
      { key: 'folio', name: 'Folio' }, { key: 'date', name: 'Fecha' }, { key: 'hospital', name: 'Hospital' }, { key: 'type', name: 'Tipo' },
      { key: 'status', name: 'Estado' }, { key: 'hasMpxr', name: 'MPXR (sí/no)' }, { key: 'quality', name: 'Clasificación' },
      { key: 'mpxr', name: 'MPXR' }, { key: 'code', name: 'Código' }, { key: 'batch', name: 'Lote' },
      { key: 'piece', name: 'Pieza' }, { key: 'priority', name: 'Prioridad' }, { key: 'reportedBy', name: 'Reportó' },
      { key: 'description', name: 'Descripción' }, { key: 'resolution', name: 'Seguimiento' },
    ],
  },
  incidentsByLot: {
    api: 'incidentsByLot', title: 'Incidencias por lote y producto', icon: 'inventory', noun: 'lotes', file: 'Incidencias-por-lote',
    search: 'Código, descripción, lote, hospital, folio…', live: ['incidents'],
    cols: [
      { key: 'code', name: 'Código' }, { key: 'description', name: 'Descripción' }, { key: 'batch', name: 'Lote' },
      { key: 'incidents', name: 'Incidencias', num: true }, { key: 'justified', name: 'Justificadas', num: true },
      { key: 'open', name: 'Sin justificar', num: true }, { key: 'mpxr', name: 'Con MPXR', num: true },
      { key: 'quality', name: 'De calidad', num: true }, { key: 'hospitals', name: 'Hospitales' },
      { key: 'last', name: 'Última' }, { key: 'folios', name: 'Folios' },
    ],
  },
  remissions: {
    api: 'remissions', title: 'Consolidación de remisiones', icon: 'receipt_long', noun: 'renglones', file: 'Consolidacion-de-remisiones',
    search: 'Remisión, hospital, doctor, CFN, lote, OC, factura…', live: ['remissions'],
    cols: [
      { key: 'remission', name: 'Remisión' }, { key: 'hospital', name: 'Hospital' }, { key: 'doctor', name: 'Doctor' },
      { key: 'consumptionDate', name: 'Fecha Consumo' }, { key: 'cfn', name: 'CFN' }, { key: 'batch', name: 'BATCH' },
      { key: 'quantity', name: 'Cant', num: true }, { key: 'alg', name: 'ALG' }, { key: 'purchaseOrder', name: 'OC' },
      { key: 'surgeryFolio', name: 'Folio de CX' }, { key: 'invoice', name: 'Factura' }, { key: 'invoiceDate', name: 'Fecha de Fac' },
      { key: 'comments', name: 'Comentarios' },
    ],
  },
  folios: {
    api: 'folios', title: 'Consolidación de folios', icon: 'assignment', noun: 'renglones', file: 'Consolidacion-de-folios',
    search: 'Folio, hospital, médico, paciente, código, lote, etiqueta…', live: ['interventions'],
    cols: [
      { key: 'folio', name: 'Folio' }, { key: 'date', name: 'Fecha' }, { key: 'hospital', name: 'Hospital' },
      { key: 'specialty', name: 'Especialidad' }, { key: 'procedure', name: 'Procedimiento' }, { key: 'doctor', name: 'Médico' },
      { key: 'patient', name: 'Paciente' }, { key: 'technician', name: 'Técnico' }, { key: 'cir', name: 'CIR' },
      { key: 'status', name: 'Estado' }, { key: 'source', name: 'Origen' }, { key: 'code', name: 'Código' },
      { key: 'description', name: 'Descripción' }, { key: 'batch', name: 'Lote' }, { key: 'expiration', name: 'Caducidad' },
      { key: 'tagRfid', name: 'Etiqueta RFID' }, { key: 'quantity', name: 'Cantidad', num: true },
    ],
  },
  deliveriesVsSap: {
    api: 'deliveriesVsSap', title: 'Solicitado vs entregado', icon: 'compare_arrows', noun: 'códigos', file: 'Solicitado-vs-entregado',
    search: 'Hospital, cuenta, CFN, lote, delivery…', live: ['shippings', 'stockSap'],
    cols: [
      { key: 'hospital', name: 'Hospital' }, { key: 'account', name: 'Cuenta SAP' }, { key: 'cfn', name: 'CFN' },
      { key: 'description', name: 'Descripción' }, { key: 'lots', name: 'Lotes' },
      { key: 'sap', name: 'Consigna SAP', num: true }, { key: 'delivered', name: 'Entregado (DLV)', num: true },
      { key: 'difference', name: 'Diferencia', num: true }, { key: 'status', name: 'Estado' }, { key: 'deliveries', name: 'Deliveries' },
    ],
  },
};

/**
 * Reportes consolidados (Consolidación de folios · Solicitado vs entregado).
 * La ruta dice cuál con `data.report`; los datos los calcula el API con los
 * permisos y hospitales del usuario.
 */
@Component({
  selector: 'app-consolidated-report',
  standalone: true,
  imports: [FormsModule, MatTableModule, MatPaginatorModule, MatSortModule, MatFormFieldModule, MatInputModule, MatSelectModule,
    MatButtonModule, MatIconModule, MatTooltipModule, MatDatepickerModule, MatDialogModule, PageLoaderComponent, PageTitleComponent,
    SearchPillComponent, KpiStripComponent],
  styleUrl: '../../logistics/logistics.scss',
  styles: [`
    td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; }
    .neg { color: #c62828; font-weight: 600; } .pos { color: #9a6b00; font-weight: 600; } .ok { color: #14853d; font-weight: 600; }
    .note { display: flex; gap: 8px; align-items: flex-start; font-size: 13px; color: #555; margin: 4px 0 10px; }
    .note mat-icon { font-size: 18px; width: 18px; height: 18px; color: #1010EB; flex-shrink: 0; }
    .unmatched { border: 1px solid #FBCCB1; background: #FDE6D8; border-radius: 8px; padding: 10px 12px; font-size: 13px; color: #6F2C05; margin-bottom: 12px; }
    .unmatched ul { margin: 6px 0 0; padding-left: 18px; }
    .bill-pending { color: #9a6b00; font-weight: 600; } .bill-issue { color: #c62828; font-weight: 600; } .bill-done { color: #14853d; font-weight: 600; }
  `],
  template: `
<app-page-title [title]="def.title" backRoute="/reports/menu" backTooltip="Regresar al menú de reportes"></app-page-title>
<app-page-loader [load]="pageLoad" [label]="def.title" (retry)="init()"></app-page-loader>

<app-kpi-strip [kpis]="kpis" [active]="activeKpi" (activeChange)="setKpi($event)"></app-kpi-strip>

<div class="table-section">
  <div class="filter-bar filter-bar--flush">
    <div class="filter-card">
      <app-search-pill [value]="searchText" [placeholder]="def.search" (valueChange)="applySearch($event)"></app-search-pill>
      <mat-form-field appearance="outline" subscriptSizing="dynamic">
        <mat-label>Periodo</mat-label>
        <mat-date-range-input [rangePicker]="picker">
          <input matStartDate [(ngModel)]="start" placeholder="Inicio" (dateChange)="reload()">
          <input matEndDate [(ngModel)]="end" placeholder="Fin" (dateChange)="reload()">
        </mat-date-range-input>
        <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
        <mat-date-range-picker #picker></mat-date-range-picker>
      </mat-form-field>
      <mat-form-field appearance="outline" subscriptSizing="dynamic">
        <mat-label>Hospital</mat-label>
        <mat-select [(value)]="place" (selectionChange)="reload()">
          <mat-option [value]="''">Todos mis hospitales</mat-option>
          @for (p of placeOptions; track p.id) { <mat-option [value]="p.id">{{ p.alias || p.name }}</mat-option> }
        </mat-select>
      </mat-form-field>
      @if (kind === 'remissions') {
        <mat-form-field appearance="outline" subscriptSizing="dynamic">
          <mat-label>Factura</mat-label>
          <mat-select [(value)]="billing" (selectionChange)="reload()">
            <mat-option [value]="''">Todas</mat-option>
            <mat-option value="PENDIENTE">Pendiente</mat-option>
            <mat-option value="CON NOVEDAD">Con novedad</mat-option>
            <mat-option value="FACTURADA">Facturada</mat-option>
          </mat-select>
        </mat-form-field>
      }
    </div>
  </div>

  @if (kind === 'deliveriesVsSap') {
    <p class="note"><mat-icon>info</mat-icon><span>
      <b>Consigna SAP</b>: último corte de Inventario SAP Consigna{{ sap ? ' (' + sap.snapshotDate + ' · ' + sap.fileName + ')' : ' (no hay corte cargado)' }}.
      <b>Entregado</b>: piezas que entraron por DLV en Recepción de material dentro del periodo. La cuenta de SAP se liga al hospital por el
      SoldTo del catálogo de Ubicaciones (o por el nombre).</span></p>
    @if (unmatched.length) {
      <div class="unmatched"><b>Cuentas de SAP sin hospital relacionado</b> — captura su SoldTo en Ubicaciones para que entren al reporte:
        <ul>@for (u of unmatched; track u.account + u.soldTo) { <li>{{ u.account || '—' }} · SoldTo {{ u.soldTo || '—' }} ({{ u.rows }} renglones)</li> }</ul>
      </div>
    }
  }

  <div class="list-actions">
    <button mat-stroked-button (click)="pageLoad.refresh(load())"><mat-icon>refresh</mat-icon> Actualizar</button>
    <button mat-flat-button color="primary" (click)="downloadExcel()" [disabled]="!dataSource.filteredData.length"><mat-icon>download</mat-icon> Descargar Excel</button>
  </div>

  <mat-paginator [pageSize]="25" [pageSizeOptions]="[25, 50, 100]" showFirstLastButtons></mat-paginator>
  <div class="table-container mat-elevation-z2">
    <table mat-table [dataSource]="dataSource" matSort>
      @for (c of def.cols; track c.key) {
        <ng-container [matColumnDef]="c.key">
          <th mat-header-cell *matHeaderCellDef mat-sort-header [class.num]="c.num">{{ c.name }}</th>
          <td mat-cell *matCellDef="let r" [class.num]="c.num" [class]="cellClass(c, r)">{{ r[c.key] ?? '' }}</td>
        </ng-container>
      }
      <ng-container matColumnDef="edit">
        <th mat-header-cell *matHeaderCellDef></th>
        <td mat-cell *matCellDef="let r"><button mat-icon-button color="primary" matTooltip="Capturar OC y factura" (click)="editBilling(r)"><mat-icon>edit</mat-icon></button></td>
      </ng-container>
      <tr mat-header-row *matHeaderRowDef="columns; sticky: true"></tr>
      <tr mat-row *matRowDef="let row; columns: columns;" class="table-row"></tr>
      <tr class="no-data-row" *matNoDataRow>
        <td [attr.colspan]="columns.length"><div class="no-data"><mat-icon>{{ def.icon }}</mat-icon><span>Sin datos en el periodo</span></div></td>
      </tr>
    </table>
  </div>
</div>
  `,
})
export class ConsolidatedReportComponent implements OnInit {
  readonly kind: string = ['deliveriesVsSap', 'remissions', 'incidentsByStatus', 'incidentsByLot'].includes(inject(ActivatedRoute).snapshot.data?.['report']) ? inject(ActivatedRoute).snapshot.data['report'] : 'folios';
  readonly def = REPORTS[this.kind];
  canEdit = false;
  columns = this.def.cols.map(c => c.key);
  billing = '';
  private readonly dialog = inject(MatDialog);
  private readonly rolePermissions = inject(RolePermissionsService);

  private readonly service = inject(ConsolidatedReportsService);
  private readonly placesService = inject(PlacesService);
  private readonly scopeService = inject(UserScopeService);
  private readonly msg = inject(CustomMessageService);
  private readonly excel = inject(SimpleExcelService);
  private scope: UserScope = { places: null, specialties: null };

  readonly pageLoad = new PageLoad();
  /** Tiempo real: recarga cuando otro usuario cambia los datos del reporte. */
  private readonly live = liveReload(this.def.live, () => this.pageLoad.ready && this.pageLoad.refresh(this.load()));

  start: Date | null = currentMonthStart();
  end: Date | null = currentMonthEnd();
  place = '';
  searchText = '';
  placeOptions: any[] = [];
  sap: { snapshotDate: string; fileName: string } | null = null;
  unmatched: { account: string; soldTo: string; rows: number }[] = [];

  dataSource = new MatTableDataSource<any>([]);
  private allRows: any[] = [];
  kpis: ListKpi[] = [];
  activeKpi: string | null = null;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  ngOnInit(): void { void this.init(); }
  init(): Promise<boolean> { return this.pageLoad.run(() => this.initialLoad()); }

  private async initialLoad(): Promise<void> {
    try { this.scope = await this.scopeService.load(); } catch { /* sin alcance */ }
    if (this.kind === 'remissions') {
      try {
        const f = await this.rolePermissions.forRoute('/reports', '/interventions');
        this.canEdit = !!(f['update'] || f['create']);
      } catch { this.canEdit = false; }
      if (this.canEdit) this.columns = [...this.columns, 'edit'];
    }
    try {
      const r: any = await this.placesService.search({ searchType: 'default', condition: { deleted: false } });
      this.placeOptions = ((r?.data ?? []) as any[])
        .filter(p => String(p.type ?? '').toUpperCase() === 'HOSPITAL' && inScope(this.scope.places, p.id, false))
        .sort((a, b) => String(a.alias || a.name).localeCompare(String(b.alias || b.name)));
    } catch { this.placeOptions = []; }
    await this.load();
  }

  reload(): void { if (this.start && this.end) void this.pageLoad.refresh(this.load()); }

  async load(): Promise<void> {
    if (!this.start || !this.end) return;
    try {
      const d = await this.service.get(this.def.api, { from: day(this.start), to: day(this.end), place: this.place,
                                                        billing: this.kind === 'remissions' ? this.billing : '' });
      this.allRows = d?.rows ?? [];
      this.sap = d?.sap ?? null;
      this.unmatched = d?.unmatchedAccounts ?? [];
      this.dataSource.filterPredicate = (row, f) => this.columns.map(k => row[k] ?? '').join(' ').toLowerCase().includes(f);
      this.refreshKpis();
      this.dataSource.paginator = this.paginator;
      this.dataSource.sort = this.sort;
      this.dataSource.filter = this.searchText.trim().toLowerCase();
    } catch (err: any) {
      await this.msg.open({ title: 'Error', type: 'error', message: err?.message || 'No se pudo generar el reporte.', dismissBtn: true });
    }
  }

  private refreshKpis(): void {
    const rows = this.allRows;
    if (this.kind === 'remissions') {
      const rems = (pred: (r: any) => boolean) => new Set(rows.filter(pred).map(r => r.remissionId)).size;
      this.kpis = [
        { key: 'total', label: 'Remisiones', icon: 'receipt_long', value: rems(() => true), isTotal: true },
        { key: 'pending', label: 'Pendientes', icon: 'schedule', value: rems(r => r.invoice === 'PENDIENTE'), tone: 'warn' },
        { key: 'issue', label: 'Con novedad', icon: 'report', value: rems(r => r.invoice === 'CON NOVEDAD'), tone: 'danger' },
        { key: 'billed', label: 'Facturadas', icon: 'task_alt', value: rems(r => !['PENDIENTE', 'CON NOVEDAD'].includes(r.invoice)), tone: 'ok' },
      ];
    } else if (this.kind === 'incidentsByStatus') {
      const n = (p: (r: any) => boolean) => rows.filter(p).length;
      this.kpis = [
        { key: 'total', label: 'Incidencias', icon: 'report_problem', value: rows.length, isTotal: true },
        { key: 'ijustified', label: 'Justificadas', icon: 'verified', value: n(r => r.status === 'Justificada'), tone: 'ok' },
        { key: 'iopen', label: 'Sin justificar', icon: 'gpp_maybe', value: n(r => r.status === 'Sin justificar'), tone: 'warn' },
        { key: 'impxr', label: 'Con MPXR', icon: 'tag', value: n(r => r.hasMpxr === 'Con MPXR') },
        { key: 'inompxr', label: 'Sin MPXR', icon: 'label_off', value: n(r => r.hasMpxr === 'Sin MPXR') },
        { key: 'iquality', label: 'De calidad', icon: 'verified_user', value: n(r => r.quality === 'CALIDAD'), tone: 'danger' },
      ];
    } else if (this.kind === 'incidentsByLot') {
      const sum = (k: string) => rows.reduce((a, r) => a + (Number(r[k]) || 0), 0);
      this.kpis = [
        { key: 'total', label: 'Lotes', icon: 'inventory', value: rows.length, isTotal: true },
        { key: 'lrepeat', label: 'Lotes con 2+ incidencias', icon: 'repeat', value: rows.filter(r => r.incidents > 1).length, tone: 'warn' },
        { key: 'lquality', label: 'Lotes con incidencias de calidad', icon: 'verified_user', value: rows.filter(r => r.quality > 0).length, tone: 'danger' },
        { key: 'lopen', label: 'Incidencias sin justificar', icon: 'gpp_maybe', value: sum('open') },
      ];
    } else if (this.kind === 'folios') {
      this.kpis = [
        { key: 'total', label: 'Folios', icon: 'assignment', value: new Set(rows.map(r => r.folio)).size, isTotal: true },
        { key: 'inv', label: 'Piezas de inventario', icon: 'nfc', value: rows.filter(r => r.source === 'Inventario').length },
        { key: 'out', label: 'Fuera de inventario', icon: 'inventory_2', value: rows.filter(r => r.source === 'Fuera de inventario').reduce((a, r) => a + (Number(r.quantity) || 0), 0), tone: 'warn' },
      ];
    } else {
      this.kpis = [
        { key: 'total', label: 'Códigos', icon: 'compare_arrows', value: rows.length, isTotal: true },
        { key: 'missing', label: 'Falta por entregar', icon: 'remove_circle', value: rows.filter(r => r.status === 'Falta por entregar').length, tone: 'danger' },
        { key: 'extra', label: 'Entregado de más', icon: 'add_circle', value: rows.filter(r => r.status === 'Entregado de más').length, tone: 'warn' },
        { key: 'ok', label: 'Completos', icon: 'task_alt', value: rows.filter(r => r.status === 'Completo').length, tone: 'ok' },
      ];
    }
    this.applyKpi();
  }

  setKpi(k: string | null): void { this.activeKpi = k; this.applyKpi(); }
  private applyKpi(): void {
    const k = this.activeKpi;
    const f: Record<string, (r: any) => boolean> = {
      inv: r => r.source === 'Inventario', out: r => r.source === 'Fuera de inventario',
      pending: r => r.invoice === 'PENDIENTE', issue: r => r.invoice === 'CON NOVEDAD',
      billed: r => !['PENDIENTE', 'CON NOVEDAD'].includes(r.invoice),
      missing: r => r.status === 'Falta por entregar', extra: r => r.status === 'Entregado de más', ok: r => r.status === 'Completo',
      iopen: r => r.status === 'Sin justificar', ijustified: r => r.status === 'Justificada', iquality: r => r.quality === 'CALIDAD',
      impxr: r => r.hasMpxr === 'Con MPXR', inompxr: r => r.hasMpxr === 'Sin MPXR', lrepeat: r => r.incidents > 1, lquality: r => r.quality > 0, lopen: r => r.open > 0,
    };
    this.dataSource.data = k && f[k] ? this.allRows.filter(f[k]) : this.allRows;
  }

  applySearch(v: string): void { this.searchText = v ?? ''; this.dataSource.filter = this.searchText.trim().toLowerCase(); }

  cellClass(c: Col, r: any): string {
    if (this.kind === 'incidentsByStatus' || this.kind === 'incidentsByLot') {
      if (c.key === 'status') return r.status === 'Justificada' ? 'ok' : 'pos';
      if (c.key === 'quality') return (c.num ? 'num ' : '') + ((c.num ? r.quality > 0 : r.quality === 'CALIDAD') ? 'neg' : '');
      return c.num ? 'num' : '';
    }
    if (c.key === 'invoice') return r.invoice === 'PENDIENTE' ? 'bill-pending' : r.invoice === 'CON NOVEDAD' ? 'bill-issue' : 'bill-done';
    if (c.key !== 'difference' && c.key !== 'status') return c.num ? 'num' : '';
    const cls = r.status === 'Falta por entregar' ? 'neg' : r.status === 'Entregado de más' ? 'pos' : 'ok';
    return (c.num ? 'num ' : '') + cls;
  }

  editBilling(r: any): void {
    this.dialog.open(RemissionBillingDialogComponent, { data: r, maxWidth: '95vw' })
      .afterClosed().subscribe(ok => { if (ok) void this.pageLoad.refresh(this.load()); });
  }

  async downloadExcel(): Promise<void> {
    const rows = this.dataSource.filteredData ?? [];
    const cols = this.def.cols.map(c => ({ columnKey: c.key, columnName: c.name, type: (c.num ? 'number' : 'string') as any }));
    await this.excel.simpleXLSX({
      fileName: `${this.def.file}-${day(this.start!)}-a-${day(this.end!)}`,
      sheets: [{ sheetName: this.def.title.slice(0, 31), columns: cols as any, data: rows, header: [], lineBreak: 0 }],
    });
  }
}

function day(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
